// Unit conversion utilities for K/M/B toggle

import type { UnitType } from "~/types/company";
import { truncateNumber } from "~/utils/numberUtils";

// Base values are stored in raw numbers (units)
// This function converts raw values to the display unit
export function convertToUnit(value: number, targetUnit: UnitType): number {
  switch (targetUnit) {
    case "K":
      return value / 1000;
    case "M":
      return value / 1000000;
    case "B":
      return value / 1000000000;
    default:
      return value;
  }
}

// Convert between units (e.g., from K to M)
export function convertBetweenUnits(
  value: number,
  fromUnit: UnitType,
  toUnit: UnitType,
): number {
  // First convert to raw value
  let rawValue: number;
  switch (fromUnit) {
    case "K":
      rawValue = value * 1000;
      break;
    case "M":
      rawValue = value * 1000000;
      break;
    case "B":
      rawValue = value * 1000000000;
      break;
    default:
      rawValue = value;
  }

  // Then convert to target unit
  return convertToUnit(rawValue, toUnit);
}

// Get conversion factor from base unit K to target unit
export function getConversionFactor(
  fromUnit: UnitType,
  toUnit: UnitType,
): number {
  const factors: Record<UnitType, number> = {
    K: 1,
    M: 0.001,
    B: 0.000001,
  };

  return factors[toUnit] / factors[fromUnit];
}

// Format a number with thousands separator (comma) and decimal point
export function formatNumberWithSeparators(
  value: number,
  decimalPlaces: number = 2,
): string {
  // Handle non-finite values (NaN, Infinity) before processing
  if (!Number.isFinite(value)) {
    return String(value); // Returns "NaN", "Infinity", or "-Infinity"
  }

  // Always truncate instead of rounding
  const truncated = truncateNumber(value, decimalPlaces);

  // Use Intl.NumberFormat for consistent formatting: commas for thousands, period for decimals
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(truncated);
}

// Format a value with proper decimals based on unit
export function formatValue(
  value: number,
  unit: UnitType,
  decimals: string = "0",
): string {
  let decimalPlaces = 0;
  if (decimals === ".0") decimalPlaces = 1;
  else if (decimals === ".00") decimalPlaces = 2;

  return formatNumberWithSeparators(value, decimalPlaces);
}

// Convert an array of data from K to target unit
export function convertDataArray(
  data: number[],
  fromUnit: UnitType,
  toUnit: UnitType,
): number[] {
  if (fromUnit === toUnit) return data;
  const factor = getConversionFactor(fromUnit, toUnit);
  // Truncate instead of rounding
  const multiplier = 10 ** 4;
  return data.map((v) => Math.trunc(v * factor * multiplier) / multiplier);
}

// Convert series data (for charts) from K to target unit
export function convertSeriesData<T extends { data: number[] }>(
  series: T[],
  fromUnit: UnitType,
  toUnit: UnitType,
): T[] {
  if (fromUnit === toUnit) return series;
  const factor = getConversionFactor(fromUnit, toUnit);
  // Truncate instead of rounding
  const multiplier = 10 ** 4;
  return series.map((s) => ({
    ...s,
    data: s.data.map((v) => Math.trunc(v * factor * multiplier) / multiplier),
  }));
}
